import type {
  GeneralInfoFormState,
  AcceptedCarrierView,
  AppointmentReason,
  InsightCheck,
  InsightView,
  InsuranceProviderView,
  OperatoryRecord,
  PriorityFatigueState,
  PriorityGoalId,
  ProviderDay,
  ProviderRecord,
  ProviderVacationRow,
  ReasonCategory,
  ReasonSentence,
  ReasonsData,
  BusinessDay,
  Carrier,
  Closure,
  GeneralInfoData,
  Operatory,
  PmProvider,
  PmProviderDay,
  Role,
} from '@elva/ui';
import {
  INSIGHT_CHECKS,
  REASONS_DATA,
  ROLE_LABEL,
  buildDefaultPriorityFatigueState,
  centsToGoalString,
  goalStringToCents,
  formatNumberWithSeparators,
} from '@elva/ui';
import type {
  CalInsightsViewApi,
  CellColorThreshold,
  EnabledFlags,
  GeneralInfo,
  KpiVisibility,
  ProductionGoals,
  SettingsAppointmentReason,
  SettingsCarrier,
  SettingsOperatory,
  SettingsProvider,
  SettingsProviderScheduleDay,
} from '../api/types';

const PROVIDER_COLORS = ['var(--success)', '#4D2855', '#8E3567', 'var(--gold)', 'var(--terracotta)'];
const OPERATORY_COLOR_KEYS = ['blue', 'green', 'red', 'amber', 'purple'] as OperatoryRecord['colorKey'][];
const VALID_OPERATORY_TYPES = new Set<OperatoryRecord['types'][number]>([
  'hygiene',
  'restorative',
  'surgical',
  'orthodontic',
  'pediatrics',
]);

const OPERATORY_TYPE_ALIASES: Record<string, OperatoryRecord['types'][number]> = {
  ortho: 'orthodontic',
  consult: 'restorative',
};

function normalizeOperatoryType(type: string): OperatoryRecord['types'][number] | null {
  const normalized = OPERATORY_TYPE_ALIASES[type] ?? type;
  return VALID_OPERATORY_TYPES.has(normalized as OperatoryRecord['types'][number])
    ? (normalized as OperatoryRecord['types'][number])
    : null;
}
const VALID_REASON_CATEGORIES = new Set<string>([
  'diagnostic',
  'emergency',
  'hygiene',
  'restorative',
  'endodontic',
  'periodontic',
  'surgical',
  'orthodontic',
]);
const PROVIDER_DAYS: ProviderDay[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const EMPTY_SCHEDULE = {
  Monday: { open: false },
  Tuesday: { open: false },
  Wednesday: { open: false },
  Thursday: { open: false },
  Friday: { open: false },
  Saturday: { open: false },
  Sunday: { open: false },
} as ProviderRecord['schedule'];

function strField(data: Record<string, unknown>, key: string, fallback = ''): string {
  const v = data[key];
  return v != null && v !== '' ? String(v) : fallback;
}

function boolField(data: Record<string, unknown>, key: string, fallback = false): boolean {
  const v = data[key];
  return typeof v === 'boolean' ? v : fallback;
}

function centsField(data: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const v = data[key];
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
  }
  return null;
}

function goalField(
  data: Record<string, unknown>,
  centsKeys: string[],
  stringKeys: string[],
): string {
  const cents = centsField(data, centsKeys);
  if (cents != null) return centsToGoalString(cents);
  for (const key of stringKeys) {
    const raw = strField(data, key);
    if (raw) return formatNumberWithSeparators(raw);
  }
  return '';
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

function toOperatoryColorKey(color: string, index: number): OperatoryRecord['colorKey'] {
  if (OPERATORY_COLOR_KEYS.includes(color as OperatoryRecord['colorKey'])) {
    return color as OperatoryRecord['colorKey'];
  }
  return OPERATORY_COLOR_KEYS[index % OPERATORY_COLOR_KEYS.length] ?? 'blue';
}

function toReasonCategory(category: string): ReasonCategory {
  return VALID_REASON_CATEGORIES.has(category) ? (category as ReasonCategory) : 'diagnostic';
}

function adaptProviderSchedule(
  schedule: SettingsProvider['schedule'],
): ProviderRecord['schedule'] {
  const result = { ...EMPTY_SCHEDULE };
  if (!schedule || Object.keys(schedule).length === 0) return result;

  const scheduleMap = schedule as Record<string, SettingsProviderScheduleDay | undefined>;
  for (const day of PROVIDER_DAYS) {
    const s =
      scheduleMap[day] ??
      scheduleMap[day.toLowerCase()] ??
      scheduleMap[day.slice(0, 3).toLowerCase()];
    if (!s) continue;
    result[day] = {
      open: Boolean(s.open),
      ...(s.start ? { start: s.start } : {}),
      ...(s.end ? { end: s.end } : {}),
    };
  }
  return result;
}

export function adaptGeneralInfo(data: GeneralInfo): GeneralInfoFormState {
  const d = data as Record<string, unknown>;
  const clinic = (d.clinic as Record<string, unknown> | undefined) ?? d;
  const logoUrl =
    strField(clinic, 'logoUrl') ||
    strField(clinic, 'logo_url') ||
    strField(clinic, 'logoURL');

  return {
    clinic: {
      name: strField(clinic, 'name'),
      tagline: strField(clinic, 'tagline'),
      address: strField(clinic, 'address'),
      phone: strField(clinic, 'phone'),
      email: strField(clinic, 'email'),
      website: strField(clinic, 'website'),
      timezone: strField(clinic, 'timezone', 'America/Los_Angeles'),
      weeklyGoal: goalField(clinic, ['weeklyGoalCents', 'weekly_goal_cents'], [
        'weekly_goal',
        'weeklyGoal',
      ]),
      monthlyGoal: goalField(clinic, ['monthlyGoalCents', 'monthly_goal_cents'], [
        'monthly_goal',
        'monthlyGoal',
      ]),
      logoPreview: logoUrl || null,
      logoFile: null,
    },
    hours: (d.hours as GeneralInfoFormState['hours']) ?? {
      monday: { open: true, start: '08:00', end: '17:00' },
      tuesday: { open: true, start: '08:00', end: '17:00' },
      wednesday: { open: true, start: '08:00', end: '17:00' },
      thursday: { open: true, start: '08:00', end: '17:00' },
      friday: { open: true, start: '08:00', end: '17:00' },
      saturday: { open: false, start: '', end: '' },
      sunday: { open: false, start: '', end: '' },
    },
    vacation: {
      enabled: boolField(d, 'vacation_enabled'),
      start: strField(d, 'vacation_start'),
      end: strField(d, 'vacation_end'),
      message: strField(d, 'vacation_message'),
    },
    providerVacations: (d.provider_vacations as GeneralInfoFormState['providerVacations']) ?? {},
    holidays: (d.holidays as GeneralInfoFormState['holidays']) ?? [],
  };
}

export function generalInfoToApi(state: GeneralInfoFormState): Partial<GeneralInfo> {
  return {
    clinic: {
      name: state.clinic.name,
      tagline: state.clinic.tagline,
      address: state.clinic.address,
      phone: state.clinic.phone,
      email: state.clinic.email,
      website: state.clinic.website,
      timezone: state.clinic.timezone,
      weeklyGoalCents: goalStringToCents(state.clinic.weeklyGoal),
      monthlyGoalCents: goalStringToCents(state.clinic.monthlyGoal),
      ...(state.clinic.logoPreview ? { logoUrl: state.clinic.logoPreview } : {}),
    },
    hours: state.hours,
    vacation_enabled: state.vacation.enabled,
    vacation_start: state.vacation.start,
    vacation_end: state.vacation.end,
    vacation_message: state.vacation.message,
    provider_vacations: state.providerVacations,
    holidays: state.holidays,
  };
}

export function adaptProviderVacationRows(providers: SettingsProvider[]): ProviderVacationRow[] {
  return providers.map((p, i) => ({
    id: p.id,
    name: p.displayName || p.display_name || `${p.firstName} ${p.lastName}`.trim(),
    role: p.role || p.specialty || 'Provider',
    initials: p.initials || initialsFromName(p.displayName),
    color: (p.color || PROVIDER_COLORS[i % PROVIDER_COLORS.length]) ?? 'var(--success)',
    picture: null,
  }));
}

export function adaptOperatories(items: SettingsOperatory[]): OperatoryRecord[] {
  return items.map((op, i) => {
    const types = (op.types ?? [])
      .map(normalizeOperatoryType)
      .filter((t): t is OperatoryRecord['types'][number] => t != null);
    return {
      id: op.id,
      shortName: op.shortName,
      displayName: op.displayName,
      active: op.active,
      colorKey: toOperatoryColorKey(op.color, i),
      types: types.length > 0 ? types : (['restorative'] as OperatoryRecord['types']),
    };
  });
}

export function operatoryToApi(record: OperatoryRecord): Partial<SettingsOperatory> {
  return {
    shortName: record.shortName,
    displayName: record.displayName,
    active: record.active,
    color: record.colorKey,
    types: record.types,
  };
}

export function adaptProviders(items: SettingsProvider[]): ProviderRecord[] {
  return items.map((p, i) => ({
    id: p.id,
    firstName: p.firstName,
    lastName: p.lastName,
    credentials: p.credentials,
    displayName: p.displayName || p.display_name || `${p.firstName} ${p.lastName}`.trim(),
    initials: p.initials || initialsFromName(p.displayName),
    color: (p.color || PROVIDER_COLORS[i % PROVIDER_COLORS.length]) ?? 'var(--success)',
    role: p.role || p.specialty || '',
    onlineBooking: p.onlineBooking,
    monthlyTarget: centsToGoalString(p.monthlyTargetCents),
    active: p.active,
    license: p.license,
    email: p.email,
    phone: p.phone,
    bio: p.bio,
    schedule: adaptProviderSchedule(p.schedule),
  }));
}

export function providerToApi(provider: ProviderRecord): Partial<SettingsProvider> {
  return {
    id: provider.id,
    firstName: provider.firstName,
    lastName: provider.lastName,
    credentials: provider.credentials,
    displayName: provider.displayName,
    initials: provider.initials,
    color: provider.color,
    role: provider.role,
    onlineBooking: provider.onlineBooking,
    monthlyTargetCents: goalStringToCents(provider.monthlyTarget),
    active: provider.active,
    license: provider.license,
    email: provider.email,
    phone: provider.phone,
    bio: provider.bio,
    schedule: provider.schedule,
  };
}

export function adaptCarriers(carriers: SettingsCarrier[]): AcceptedCarrierView[] {
  return carriers.map((c) => ({
    id: String(c.id),
    planTypes: ['PPO'],
    notes: '',
    active: c.active,
    inNetworkProviders: 0,
  }));
}

export function adaptInsuranceProviders(providers: SettingsProvider[]): InsuranceProviderView[] {
  return providers.map((p, i) => ({
    id: p.id,
    name: p.displayName || p.display_name || `${p.firstName} ${p.lastName}`.trim(),
    initials: p.initials || initialsFromName(p.displayName),
    role: p.role || p.specialty || 'Provider',
    color: (p.color || PROVIDER_COLORS[i % PROVIDER_COLORS.length]) ?? 'var(--success)',
  }));
}

export function adaptPriorityFatigue(
  flags: EnabledFlags | undefined,
  thresholds: CellColorThreshold[],
  kpi: KpiVisibility | undefined,
  goals?: ProductionGoals | undefined,
): PriorityFatigueState {
  const defaults = buildDefaultPriorityFatigueState();
  const validGoalIds = new Set<PriorityGoalId>(defaults.order);
  const threshold = thresholds[0];

  const normalizeOrder = (raw: string[] | undefined): PriorityGoalId[] => {
    if (!raw?.length) return defaults.order;
    const filtered = raw.filter((id): id is PriorityGoalId => validGoalIds.has(id as PriorityGoalId));
    if (filtered.length === 0) return defaults.order;
    const missing = defaults.order.filter((goalId: PriorityGoalId) => !filtered.includes(goalId));
    return [...filtered, ...missing];
  };

  const parseFatigueFromKpi = (): number | undefined => {
    const marker = kpi?.visible?.find((entry) => entry.startsWith('fatigue:'));
    if (!marker) return undefined;
    const parsed = Number(marker.split(':')[1]);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const safeFromGoals = goals?.safe_mode;
  const safe = {
    judgeRejectRate:
      safeFromGoals?.judge_reject_rate ??
      threshold?.threshold ??
      defaults.safe.judgeRejectRate,
    overrideRate: safeFromGoals?.override_rate ?? defaults.safe.overrideRate,
    errorRate: safeFromGoals?.error_rate ?? defaults.safe.errorRate,
    pmsStaleHours: safeFromGoals?.pms_stale_hours ?? defaults.safe.pmsStaleHours,
    autoRecover: safeFromGoals?.auto_recover ?? defaults.safe.autoRecover,
  };

  const order = normalizeOrder(
    goals?.priority_order ?? flags?.enabled ?? kpi?.visible?.filter((id) => validGoalIds.has(id as PriorityGoalId)),
  );

  const fatigue =
    goals?.fatigue_sensitivity ??
    parseFatigueFromKpi() ??
    defaults.fatigue;

  const judge =
    goals?.judge_strictness ??
    (kpi?.visible?.includes('judge:strict')
      ? 'strict'
      : kpi?.visible?.includes('judge:lenient')
        ? 'lenient'
        : defaults.judge);

  return {
    order,
    fatigue,
    judge,
    safe,
  };
}

export function priorityFatigueToProductionGoals(state: PriorityFatigueState): ProductionGoals {
  return {
    priority_order: state.order,
    fatigue_sensitivity: state.fatigue,
    judge_strictness: state.judge,
    safe_mode: {
      judge_reject_rate: state.safe.judgeRejectRate,
      override_rate: state.safe.overrideRate,
      error_rate: state.safe.errorRate,
      pms_stale_hours: state.safe.pmsStaleHours,
      auto_recover: state.safe.autoRecover,
    },
  };
}

export function priorityFatigueToEnabledFlags(state: PriorityFatigueState): { enabled: string[] } {
  return { enabled: state.order };
}

export function priorityFatigueToCellColorPatch(
  state: PriorityFatigueState,
  threshold?: CellColorThreshold,
): Partial<CellColorThreshold> | undefined {
  if (!threshold) return undefined;
  return { threshold: state.safe.judgeRejectRate };
}

export function priorityFatigueToKpiVisibility(state: PriorityFatigueState): Partial<KpiVisibility> {
  const visible = [
    ...state.order,
    `fatigue:${state.fatigue}`,
    `judge:${state.judge}`,
  ];
  return { visible };
}

export function buildProviderNetworkMap(
  providers: SettingsProvider[],
  networks: Array<{ providerId: string; carrierIds: string[] }>,
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const p of providers) map[p.id] = [];
  for (const entry of networks) {
    map[entry.providerId] = entry.carrierIds;
  }
  return map;
}

/* ─── Settings · Practice management (spec 15 §4–§7) ─────────────────────────
 * Server data ↔ stage's practice-settings views. Only fields the server has are
 * written back; everything else the views show is derived or a documented
 * default (docs/cherry-pick-fake-data.md, F19).
 */

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const IANA_TO_TZ_LABEL: Record<string, string> = {
  'America/Chicago': 'Central Time (US & Canada)',
  'America/New_York': 'Eastern Time (US & Canada)',
  'America/Denver': 'Mountain Time (US & Canada)',
  'America/Los_Angeles': 'Pacific Time (US & Canada)',
};
const TZ_LABEL_TO_IANA: Record<string, string> = Object.fromEntries(
  Object.entries(IANA_TO_TZ_LABEL).map(([iana, label]) => [label, iana]),
);

/** "08:00" / "8:00 AM" → "8:00 am" (General info) or "8:00" (provider days). */
function displayTime(raw: string, withSuffix: boolean): string {
  const t = raw.trim();
  const twelve = /^(\d{1,2}):(\d{2})\s*(am|pm)$/i.exec(t);
  const twentyFour = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(t);
  let h: number;
  let m: string;
  let suffix: string;
  if (twelve) {
    h = Number(twelve[1]);
    m = twelve[2]!;
    suffix = twelve[3]!.toLowerCase();
  } else if (twentyFour) {
    const h24 = Number(twentyFour[1]);
    m = twentyFour[2]!;
    suffix = h24 >= 12 ? 'pm' : 'am';
    h = h24 % 12 === 0 ? 12 : h24 % 12;
  } else {
    return t;
  }
  return withSuffix ? `${String(h)}:${m} ${suffix}` : `${String(h)}:${m}`;
}

type RawDay = Record<string, unknown>;

function rawHours(data: GeneralInfo): Record<string, RawDay> {
  const hours = (data as Record<string, unknown>).hours;
  return hours && typeof hours === 'object' ? (hours as Record<string, RawDay>) : {};
}

/** The key the server used for a weekday ("Monday" or "monday"). */
function dayKey(hours: Record<string, RawDay>, label: string): string {
  if (label in hours) return label;
  if (label.toLowerCase() in hours) return label.toLowerCase();
  return label;
}

function rawString(v: unknown): string {
  return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
}

export function adaptPracticeGeneralInfo(data: GeneralInfo): GeneralInfoData {
  const form = adaptGeneralInfo(data);
  const d = data as Record<string, unknown>;
  const hours = rawHours(data);
  const name = form.clinic.name;

  const businessHours: BusinessDay[] = WEEKDAYS.map((label, day) => {
    const raw = hours[dayKey(hours, label)];
    const open = Boolean(raw?.open);
    const from = rawString(raw?.openTime ?? raw?.start);
    const to = rawString(raw?.closeTime ?? raw?.end);
    return {
      day,
      label,
      open,
      from: open && from ? displayTime(from, true) : '',
      to: open && to ? displayTime(to, true) : '',
    };
  });

  const closures: Closure[] = form.holidays.map((h) => ({
    id: `h${String(h.id)}`,
    reason: h.name,
    start: h.date,
    end: h.date,
  }));
  if (form.vacation.enabled || form.vacation.start) {
    closures.unshift({
      id: 'vacation',
      reason: 'Vacation',
      start: form.vacation.start,
      end: form.vacation.end || null,
      ...(form.vacation.message ? { script: form.vacation.message } : {}),
      runningNow: form.vacation.enabled,
    });
  }

  return {
    logoInitials: initialsFromName(name || 'Practice'),
    practiceName: name,
    tagline: form.clinic.tagline,
    address: form.clinic.address,
    phone: form.clinic.phone,
    supportEmail: form.clinic.email,
    website: form.clinic.website,
    timezone: IANA_TO_TZ_LABEL[form.clinic.timezone] ?? form.clinic.timezone,
    goalWeek: form.clinic.weeklyGoal,
    goalMonth: form.clinic.monthlyGoal,
    hours: businessHours,
    closures,
    // Not stored by the settings API: stage's template with this practice's name (F19).
    callDisclosure:
      strField(d, 'call_disclosure') ||
      `Hi, this is Elva, an automated assistant calling on behalf of ${name || 'the practice'}. This call is recorded.`,
    disclosureVersion: strField(d, 'call_disclosure_version'),
    reviewLink: strField(d, 'review_link'),
    bookingLink: strField(d, 'booking_link'),
  };
}

/** Apply the view's edits onto the last server payload; only server-backed fields change. */
export function practiceGeneralInfoToApi(next: GeneralInfoData, base: GeneralInfo): Partial<GeneralInfo> {
  const form = adaptGeneralInfo(base);
  form.clinic = {
    ...form.clinic,
    name: next.practiceName,
    tagline: next.tagline,
    address: next.address,
    phone: next.phone,
    email: next.supportEmail,
    website: next.website,
    timezone: TZ_LABEL_TO_IANA[next.timezone] ?? next.timezone,
    weeklyGoal: next.goalWeek,
    monthlyGoal: next.goalMonth,
  };
  form.holidays = form.holidays.filter((h) => next.closures.some((c) => c.id === `h${String(h.id)}`));
  const vacation = next.closures.find((c) => c.id === 'vacation');
  form.vacation = vacation
    ? { ...form.vacation, message: vacation.script ?? form.vacation.message }
    : { ...form.vacation, enabled: false };

  const hours = { ...rawHours(base) };
  for (const day of next.hours) {
    const key = dayKey(hours, day.label);
    hours[key] = { ...(hours[key] ?? {}), open: day.open };
  }
  return { ...generalInfoToApi(form), hours };
}

const ROLE_MATCHERS: [RegExp, Role][] = [
  [/hygien/i, 'hygienist'],
  [/ortho/i, 'orthodontist'],
  [/perio/i, 'periodontist'],
  [/endo/i, 'endodontist'],
  [/surg/i, 'oral_surgeon'],
  [/p(a)?ediatric|pedo/i, 'paediatric_dentist'],
  [/prosth/i, 'prosthodontist'],
  [/dentist|dds|dmd|general/i, 'general_dentist'],
];

function rolesFromServer(...values: (string | undefined)[]): Role[] {
  const out: Role[] = [];
  for (const v of values) {
    if (!v) continue;
    const hit = ROLE_MATCHERS.find(([re]) => re.test(v));
    if (hit && !out.includes(hit[1])) out.push(hit[1]);
  }
  return out;
}

export function adaptPracticeProviders(items: SettingsProvider[]): PmProvider[] {
  const records = adaptProviders(items);
  return items.map((p, i) => {
    const r = records[i]!;
    const fullName = `${p.firstName} ${p.lastName}`.trim() || r.displayName;
    const days: PmProviderDay[] = PROVIDER_DAYS.map((label) => {
      const day = r.schedule[label];
      return day.open
        ? {
            label,
            working: true,
            ...(day.start ? { from: displayTime(day.start, false) } : {}),
            ...(day.end ? { to: displayTime(day.end, false) } : {}),
          }
        : { label, working: false };
    });
    return {
      id: r.id,
      name: p.credentials ? `${fullName}, ${p.credentials}` : fullName,
      initials: r.initials,
      colour: r.color,
      elvaSays: r.displayName,
      roles: rolesFromServer(p.role, p.specialty),
      bio: r.bio,
      days,
      // No server fields for these: stage defaults (F19).
      timeOff: [],
      monthlyTarget: r.monthlyTarget,
      status: r.active ? 'working' : 'away',
      onlineBooking: r.onlineBooking,
      acceptingNewPatients: true,
      mayBeDoubleBooked: false,
      showInCalendar: r.active,
      onRosterSince: '—',
    };
  });
}

export function practiceProviderToApi(next: PmProvider, original: SettingsProvider): Partial<SettingsProvider> {
  const base = providerToApi(adaptProviders([original])[0]!);
  const roleChanged =
    next.roles.length > 0 && next.roles.join() !== rolesFromServer(original.role, original.specialty).join();
  return {
    ...base,
    displayName: next.elvaSays,
    bio: next.bio,
    onlineBooking: next.onlineBooking,
    monthlyTargetCents: goalStringToCents(next.monthlyTarget),
    active: next.showInCalendar,
    ...(roleChanged ? { role: ROLE_LABEL[next.roles[0]!] } : {}),
  };
}

const OPERATORY_TYPE_TO_ROLE: Record<OperatoryRecord['types'][number], Role> = {
  hygiene: 'hygienist',
  restorative: 'general_dentist',
  surgical: 'oral_surgeon',
  orthodontic: 'orthodontist',
  pediatrics: 'paediatric_dentist',
};

export function adaptPracticeOperatories(items: SettingsOperatory[]): Operatory[] {
  return adaptOperatories(items).map((op) => ({
    id: op.id,
    name: op.displayName || op.shortName,
    acceptsRoles: Array.from(new Set(op.types.map((t) => OPERATORY_TYPE_TO_ROLE[t]))),
    outOfService: !op.active,
  }));
}

export function practiceOperatoryToApi(next: Operatory, original: SettingsOperatory): Partial<SettingsOperatory> {
  const record = adaptOperatories([original])[0]!;
  const types = (Object.keys(OPERATORY_TYPE_TO_ROLE) as OperatoryRecord['types']).filter((t) =>
    next.acceptsRoles.includes(OPERATORY_TYPE_TO_ROLE[t]),
  );
  return {
    ...operatoryToApi(record),
    displayName: next.name,
    active: !next.outOfService,
    ...(types.length > 0 ? { types } : {}),
  };
}

export function adaptPracticeCarriers(items: SettingsCarrier[]): Carrier[] {
  return items.map((c, i) => ({
    id: String(c.id),
    name: c.name,
    // Payer ID, aliases, plan types and plans are not stored by the settings API (F19).
    payerId: '—',
    initials: initialsFromName(c.name),
    colour: PROVIDER_COLORS[i % PROVIDER_COLORS.length] ?? 'var(--success)',
    aliases: [c.name],
    planTypes: [],
    plans: [{ id: `${String(c.id)}-all`, name: c.name, aliases: [], status: 'in_network' }],
    unlisted: [],
    paused: !c.active,
    // Provider networks are write-only on the API, so there is nothing to read back.
    inNetworkProviderIds: [],
  }));
}

/**
 * The 39 checks are the same catalogue the calendar-insights API already stores,
 * in the same order, under the previous item keys. `financial` was `rcm`.
 */
const INSIGHT_SERVER_KEYS: Record<InsightView, string[]> = {
  daily: [
    'overall_day_flow',
    'provider_workload',
    'new_patients_today',
    'hygiene_recare',
    'family_overdue',
    'same_day_retention',
    'scheduling_bottlenecks',
    'emergency_slots',
    'same_day_upsell',
    'financial_prep',
    'day_end_followup',
  ],
  weekly: [
    'week_production_proj',
    'provider_load_balance',
    'open_chair_hours',
    'big_cases_week',
    'hygiene_reappt_rate',
    'family_chains',
    'long_gap_returners',
    'lab_case_deadlines',
    'pre_auth_pending',
    'pediatric_consent',
    'forms_not_sent',
    'expiring_benefits',
    'deductible_met',
    'weekly_collections',
  ],
  monthly: [
    'production_gap',
    'daily_avg_needed',
    'provider_month_attain',
    'hygiene_reappt_vs_goal',
    'unscheduled_tx_total',
    'patient_birthdays',
    'referrals_not_contacted',
    'license_renewals',
    'osha_log',
    'compliance_deadlines',
    'aging_ar',
    'ytd_unused_benefits',
    'insurance_writeoffs',
    'unsigned_tx_plans',
  ],
};

const INSIGHT_VIEWS: InsightView[] = ['daily', 'weekly', 'monthly'];

export function insightViewsRecord(
  views: Record<string, CalInsightsViewApi> | CalInsightsViewApi[] | unknown,
): Record<string, CalInsightsViewApi> {
  if (!views || typeof views !== 'object') return {};
  if (Array.isArray(views)) {
    return Object.fromEntries(
      views
        .filter((v): v is CalInsightsViewApi => Boolean(v && typeof v === 'object'))
        .map((v) => [v.view, v]),
    );
  }
  return views as Record<string, CalInsightsViewApi>;
}

/** Stage's check list, with each view's ticks taken from `enabled_items` when that view exists. */
export function adaptInsightChecks(
  views: Record<string, CalInsightsViewApi> | CalInsightsViewApi[] | unknown,
): InsightCheck[] {
  const record = insightViewsRecord(views);
  const checks = INSIGHT_CHECKS.map((c) => ({ ...c }));
  for (const view of INSIGHT_VIEWS) {
    const raw = record[view];
    if (!raw) continue;
    const enabled = new Set(raw.enabled_items ?? []);
    const keys = INSIGHT_SERVER_KEYS[view];
    let index = 0;
    for (const check of checks) {
      if (check.view !== view) continue;
      const key = keys[index];
      index += 1;
      if (key) check.enabled = enabled.has(key);
    }
  }
  return checks;
}

export function insightChecksToApi(
  view: InsightView,
  checks: InsightCheck[],
  raw: CalInsightsViewApi | undefined,
): CalInsightsViewApi {
  const keys = INSIGHT_SERVER_KEYS[view];
  const inView = checks.filter((c) => c.view === view);
  const enabled_items = inView.flatMap((check, index) => {
    const key = keys[index];
    return check.enabled && key ? [key] : [];
  });
  return { ...(raw ?? { view }), view, enabled_items };
}

function rolesOnReason(reason: SettingsAppointmentReason, providers: SettingsProvider[]): Role[] {
  const ids = new Set([...(reason.primaryProviderIds ?? []), ...(reason.secondaryProviderIds ?? [])]);
  const roles: Role[] = [];
  for (const provider of providers) {
    if (!ids.has(provider.id)) continue;
    for (const role of rolesFromServer(provider.role, provider.specialty)) {
      if (!roles.includes(role)) roles.push(role);
    }
  }
  return roles;
}

export function adaptBookingReasons(
  reasons: SettingsAppointmentReason[],
  providers: SettingsProvider[],
): AppointmentReason[] {
  return reasons.map((r) => ({
    id: r.id,
    code: r.code || r.id.replace(/^r_/, '').toUpperCase().slice(0, 4),
    displayName: r.name,
    category: toReasonCategory(r.category),
    durationMinutes: r.duration,
    bufferAfterMinutes: r.buffer ?? 0,
    // Not stored by the appointment-reasons API (F20).
    earliestBookable: 'Next open slot',
    roles: rolesOnReason(r, providers),
    // The API only has bookable-online. ELVA & staff cannot be told apart from Anyone (F20).
    bookableBy: r.bookableOnline ? 'anyone' : 'staff_only',
    cdtCodes: r.cdtCodes ?? [],
    ...(r.patientDescription ? { patientDescription: r.patientDescription } : {}),
    ...(r.aiDescription ? { elvaDescription: r.aiDescription } : {}),
    bookable: r.active,
  }));
}

/** Sentences have no API. Stage's list, retargeted when a real reason shares the mock code (F20). */
export function bookingSentencesFor(reasons: AppointmentReason[]): ReasonSentence[] {
  const codeByMockId = new Map(REASONS_DATA.reasons.map((r) => [r.id, r.code.trim().toUpperCase()]));
  const idByCode = new Map(reasons.map((r) => [r.code.trim().toUpperCase(), r.id]));
  return REASONS_DATA.sentences.map((sentence) => {
    const code = codeByMockId.get(sentence.reasonId);
    const reasonId = code ? idByCode.get(code) : undefined;
    return reasonId ? { ...sentence, reasonId } : sentence;
  });
}

export function adaptBookingReasonsData(
  reasons: SettingsAppointmentReason[],
  providers: SettingsProvider[],
): ReasonsData {
  const adapted = adaptBookingReasons(reasons, providers);
  return {
    reasons: adapted,
    sentences: bookingSentencesFor(adapted),
    waitingFromCalls: REASONS_DATA.waitingFromCalls,
    waitingCloseCall: REASONS_DATA.waitingCloseCall,
    waitingNothingNear: REASONS_DATA.waitingNothingNear,
  };
}

function sameRoles(a: Role[], b: Role[]): boolean {
  return a.length === b.length && a.every((role) => b.includes(role));
}

export function bookingReasonToApi(
  next: AppointmentReason,
  original: SettingsAppointmentReason,
  providers: SettingsProvider[],
): Partial<SettingsAppointmentReason> {
  const rolesUnchanged = sameRoles(next.roles, rolesOnReason(original, providers));
  const primaryProviderIds = rolesUnchanged
    ? original.primaryProviderIds
    : providers
        .filter((provider) =>
          next.roles.some((role) => rolesFromServer(provider.role, provider.specialty).includes(role)),
        )
        .map((provider) => provider.id);
  return {
    id: original.id,
    code: next.code,
    name: next.displayName,
    category: next.category,
    duration: next.durationMinutes,
    buffer: next.bufferAfterMinutes,
    color: original.color,
    cdtCodes: next.cdtCodes,
    bookableOnline: next.bookableBy !== 'staff_only',
    active: next.bookable,
    patientDescription: next.patientDescription ?? '',
    aiDescription: next.elvaDescription ?? '',
    primaryProviderIds,
    secondaryProviderIds: rolesUnchanged ? original.secondaryProviderIds : [],
    operatoryIds: original.operatoryIds,
    price: original.price ?? null,
  };
}
